import { useLocalization } from '@fluent/react';
import { Box, Chip, InputAdornment, TextField, Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { FC } from 'react';

import styles from './styles';

export interface RsuOption {
  id: string;
  name: string;
  downtimeHours?: number;
}

export interface RsuMultiSelectProps {
  options: RsuOption[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  tooltip?: string;
}

const RsuMultiSelect: FC<RsuMultiSelectProps> = (props) => {
  const { l10n } = useLocalization();
  const classes = styles();

  const selectedOptions = props.options.filter((option) => props.selectedIds.includes(option.id));

  const handleChange = (event: React.ChangeEvent<{}>, value: RsuOption[]) => {
    const selectedIds = value.map((option) => option.id);
    props.onChange(selectedIds);
  };

  return (
    <Autocomplete
      multiple
      options={props.options}
      value={selectedOptions}
      onChange={handleChange}
      getOptionLabel={(option) => option.name}
      getOptionSelected={(option, value) => option.id === value.id}
      disabled={props.disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            props.placeholder || l10n.getString('dashboard-rsu-downtimes-rsu-placeholder')
          }
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon style={{ color: '#82919b' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))
      }
      renderOption={(option) => (
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="body2">{option.name}</Typography>
          {option.downtimeHours !== undefined && (
            <Typography variant="body2" style={{ color: '#82919b', marginLeft: 8, whiteSpace: 'nowrap' }}>
              {option.downtimeHours.toFixed(1)} h
            </Typography>
          )}
        </Box>
      )}
      filterOptions={(options, { inputValue }) =>
        options.filter((option) => option.name.toLowerCase().includes(inputValue.toLowerCase()))
      }
      noOptionsText={l10n.getString('dashboard-rsu-downtimes-no-rsu-found')}
      className={classes.autocomplete}
    />
  );
};

export default React.memo(RsuMultiSelect);
